import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Card,
  Button,
  Input,
  Chip,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tabs,
  Tab,
  Select,
  SelectItem,
  Switch,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  PageLayout,
  StatusChip,
  useConfirm,
} from '../../../components/ui';
import CustomAutocomplete from '../../../components/common/CustomAutocomplete';
import { Ship, ShipTrip, Market } from 'common/src/utils/types';
import {
  getShips,
  createShip,
  updateShip,
  deleteShip,
} from 'common/src/api/ships';
import {
  getShipTrips,
  updateShipTrip,
  deleteShipTrip,
  createShipTrip,
} from 'common/src/api/ship_trips';
import {
  createMarket,
  getMarkets,
  suggestMarkets,
} from 'common/src/api/markets';
import { toast } from 'react-toastify';
import {
  updateSettlementShipTripByShipTripId,
} from 'common/src/api/settlements';
import * as XLSX from 'xlsx';

type ShipType = 'A' | 'M' | 'T';

type BulkShipUploadRow = {
  rowNumber: number;
  shipName: string;
  shipType: ShipType | null;
  etd: string | null;
  eta: string | null;
  originLocode: string;
  destinationLocode: string;
  errors: string[];
  warnings: string[];
};

type BulkShipUploadPreviewRow = BulkShipUploadRow & {
  id: string;
  matchedShip?: Ship;
  shipMatchScore?: number;
  shipAction: 'create' | 'reuse' | 'error';
  matchedTrip?: ShipTrip;
  tripAction: 'create' | 'reuse' | 'duplicate-file' | 'error';
  duplicateOfRow?: number;
};

type MissingMarketDraft = {
  id: string;
  name: string;
  country: string;
  country_code: string;
  reviewed: boolean;
  primary_market_id: string | null;
};

const BULK_UPLOAD_HEADERS = [
  'Nave',
  'Tipo',
  'ETD',
  'ETA',
  'ORIGEN LOCODE',
  'DESTINO LOCODE',
];

const normalizeText = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeHeader = (value: unknown) => normalizeText(value);

const normalizeShipName = (value: unknown) =>
  normalizeText(value)
    .replace(/\b(MV|M V|VESSEL|NAVE|SHIP)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeLocode = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const getCountryFromLocode = (locode: string) => {
  const countryCode = normalizeLocode(locode).slice(0, 2);
  if (countryCode.length !== 2) return '';

  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'region' })
        .of(countryCode)
        ?.toUpperCase() || ''
    );
  } catch {
    return countryCode;
  }
};

const getCountryCodeFromLocode = (locode: string) =>
  normalizeLocode(locode).slice(0, 2);

const parseShipType = (value: unknown): ShipType | null => {
  const normalized = normalizeText(value);
  if (['A', 'AEREO', 'AERERO', 'AIR', 'AIRPLANE'].includes(normalized)) {
    return 'A';
  }
  if (['M', 'MARITIMO', 'MARITIMA', 'SEA', 'OCEAN'].includes(normalized)) {
    return 'M';
  }
  if (['T', 'TERRESTRE', 'TRUCK', 'ROAD', 'CAMION'].includes(normalized)) {
    return 'T';
  }
  return null;
};

const excelSerialDateToYMD = (serial: number) => {
  const utcDays = Math.floor(serial - 25569);
  const date = new Date(utcDays * 86400 * 1000);
  return date.toISOString().split('T')[0];
};

const dateToYMD = (date: Date) => {
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

const parseBulkUploadDate = (value: unknown): string | null => {
  if (value instanceof Date) {
    return dateToYMD(value);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return excelSerialDateToYMD(value);
  }

  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const isoMatch = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return dateToYMD(
      new Date(Number(year), Number(month) - 1, Number(day), 12)
    );
  }

  const dmyMatch = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (dmyMatch) {
    const [, day, month, yearRaw] = dmyMatch;
    const year =
      yearRaw.length === 2 ? Number(`20${yearRaw}`) : Number(yearRaw);
    return dateToYMD(new Date(year, Number(month) - 1, Number(day), 12));
  }

  return dateToYMD(new Date(raw));
};

const levenshteinDistance = (a: string, b: string) => {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;

  const previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  const current = Array.from({ length: b.length + 1 }, () => 0);

  for (let i = 1; i <= a.length; i++) {
    current[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + cost
      );
    }
    previous.splice(0, previous.length, ...current);
  }

  return previous[b.length];
};

const similarityScore = (a: string, b: string) => {
  const normalizedA = normalizeShipName(a);
  const normalizedB = normalizeShipName(b);
  const maxLength = Math.max(normalizedA.length, normalizedB.length);
  if (maxLength === 0) return 0;
  return 1 - levenshteinDistance(normalizedA, normalizedB) / maxLength;
};

const isYMDWithinDays = (candidate: string, target: string, days: number) => {
  const candidateTime = new Date(`${candidate.split('T')[0]}T12:00:00`).getTime();
  const targetTime = new Date(`${target}T12:00:00`).getTime();
  return (
    Math.abs(candidateTime - targetTime) <= days * 24 * 60 * 60 * 1000
  );
};

const SuperadminShips = () => {
  const [ships, setShips] = useState<Ship[]>([]);
  const confirm = useConfirm();
  const [shipTrips, setShipTrips] = useState<ShipTrip[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [filteredShipTrips, setFilteredShipTrips] = useState<ShipTrip[]>([]);
  const [filteredShips, setFilteredShips] = useState<Ship[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('ships');

  // Modal states
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingShip, setEditingShip] = useState<Ship | null>(null);
  const [editingShipTrip, setEditingShipTrip] = useState<ShipTrip | null>(null);
  const [newShipData, setNewShipData] = useState({
    name: '',
    company: '',
    type: '',
  });

  // Add a new modal disclosure
  const shipTripModal = useDisclosure();

  // Add new state for editing ship trip data
  const [editingShipTripData, setEditingShipTripData] = useState({
    voyage_number: '',
    origin_market_id: '',
    etd: '',
    eta: '',
    market_id: '',
  });

  const [mergeMode, setMergeMode] = useState(false);
  const [selectedShips, setSelectedShips] = useState<number[]>([]);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [newMergedShipName, setNewMergedShipName] = useState('');

  // Add new state for new ship trip data
  const [newShipTripData, setNewShipTripData] = useState({
    voyage_number: '',
    origin_market_id: '',
    etd: '',
    eta: '',
    market_id: '',
  });

  // Add new modal disclosure for creating ship trips
  const newShipTripModal = useDisclosure();
  const bulkUploadModal = useDisclosure();

  // Add new state variables for trip merging
  const [tripMergeMode, setTripMergeMode] = useState(false);
  const [selectedTrips, setSelectedTrips] = useState<number[]>([]);
  const [tripMergeModalOpen, setTripMergeModalOpen] = useState(false);
  const [newMergedTripDates, setNewMergedTripDates] = useState({
    etd: '',
    eta: '',
  });

  const [bulkUploadFileName, setBulkUploadFileName] = useState('');
  const [bulkUploadRows, setBulkUploadRows] = useState<BulkShipUploadRow[]>([]);
  const [bulkUploadPreviewRows, setBulkUploadPreviewRows] = useState<
    BulkShipUploadPreviewRow[]
  >([]);
  const [selectedBulkTripRows, setSelectedBulkTripRows] = useState<Set<string>>(
    new Set()
  );
  const [missingMarketDrafts, setMissingMarketDrafts] = useState<
    MissingMarketDraft[]
  >([]);
  const [bulkUploadErrors, setBulkUploadErrors] = useState<string[]>([]);
  const [isParsingBulkUpload, setIsParsingBulkUpload] = useState(false);
  const [isConfirmingBulkUpload, setIsConfirmingBulkUpload] = useState(false);
  const [isSuggestingMarkets, setIsSuggestingMarkets] = useState(false);
  const [isCreatingMarkets, setIsCreatingMarkets] = useState(false);

  // Calendar state
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [calendarShipFilter, setCalendarShipFilter] = useState<string>('');

  // Global ship type filter (A, M, T)
  const [shipTypeFilter, setShipTypeFilter] = useState<string>('');

  // Resource timeline specific filters
  const [resourceSearchTerm, setResourceSearchTerm] = useState<string>('');
  const [resourceSort, setResourceSort] = useState<'name' | 'etd' | 'eta'>(
    'name'
  );

  // Add these state variables near the other modal states
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [originTripId, setOriginTripId] = useState<number | null>(null);
  const [destinationTripId, setDestinationTripId] = useState<number | null>(
    null
  );

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Helper for translating type to label used in search
    const typeToLabel = (t?: string) => {
      if (t === 'A') return 'Aéreo';
      if (t === 'M') return 'Marítimo';
      if (t === 'T') return 'Terrestre';
      return '';
    };

    setFilteredShips(
      ships
        .filter((ship) => !shipTypeFilter || ship.type === shipTypeFilter)
        .filter(
          (ship) =>
            ship.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            typeToLabel(ship.type)
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
        )
    );
    setFilteredShipTrips(
      shipTrips
        .filter((trip) => !shipTypeFilter || trip.ship?.type === shipTypeFilter)
        .filter(
          (trip) =>
            trip.ship?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            trip.etd?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            trip.eta?.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
  }, [searchTerm, ships, shipTrips, shipTypeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shipsData, shipTripsData, marketsData] = await Promise.all([
        getShips(),
        getShipTrips(),
        getMarkets(),
      ]);

      // Enhance ships with their trips
      const enhancedShips = shipsData.map((ship) => ({
        ...ship,
        ship_trips: shipTripsData.filter((trip) => trip.ship_id === ship.id),
      }));

      // console.log(enhancedShips);

      setShips(enhancedShips);
      setShipTrips(
        shipTripsData.sort((a, b) => {
          const nameA = a.ship?.name || '';
          const nameB = b.ship?.name || '';
          return nameA.localeCompare(nameB);
        })
      );
      setMarkets(marketsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleEditShip = async (ship: Ship) => {
    setEditingShip(ship);
    setNewShipData({
      name: ship.name,
      company: ship.company || '',
      type: ship.type || '',
    });
    onOpen();
  };

  const handleSaveShip = async () => {
    try {
      if (editingShip) {
        await updateShip(editingShip.id, newShipData);
        toast.success('Nave actualizada correctamente');
      } else {
        await createShip(newShipData);
        toast.success('Nave creada correctamente');
      }
      fetchData();
      onClose();
    } catch (error) {
      console.error('Error saving ship:', error);
      toast.error('Error al guardar la nave');
    }
  };

  const handleDeleteShip = async (id: number) => {
    // Find the ship and check if it has any trips
    const ship = ships.find((s) => s.id === id);
    if (ship?.ship_trips && ship.ship_trips.length > 0) {
      toast.error('No se puede eliminar una nave con viajes asociados');
      return;
    }

    if (await confirm({ title: '¿Eliminar esta nave?', isDestructive: true })) {
      try {
        await deleteShip(id);
        toast.success('Nave eliminada correctamente');
        fetchData();
      } catch (error) {
        console.error('Error deleting ship:', error);
        toast.error('Error al eliminar la nave');
      }
    }
  };

  const handleEditShipTrip = async (shipTrip: ShipTrip) => {
    setEditingShipTrip(shipTrip);
    setEditingShipTripData({
      voyage_number: shipTrip.voyage_number || '',
      origin_market_id: shipTrip.origin_market_id || '',
      etd: shipTrip.etd ? shipTrip.etd.split('T')[0] : '',
      eta: shipTrip.eta ? shipTrip.eta.split('T')[0] : '',
      market_id: shipTrip.market_id || '',
    });
    shipTripModal.onOpen();
  };

  const handleSaveShipTrip = async () => {
    try {
      if (!editingShipTrip) return;

      await updateShipTrip(editingShipTrip.id, {
        voyage_number: editingShipTripData.voyage_number,
        origin_market_id: editingShipTripData.origin_market_id,
        etd: new Date(editingShipTripData.etd).toISOString(),
        eta: new Date(editingShipTripData.eta).toISOString(),
        market_id: editingShipTripData.market_id,
      });

      toast.success('Viaje actualizado correctamente');
      fetchData();
      shipTripModal.onClose();
    } catch (error) {
      console.error('Error saving ship trip:', error);
      toast.error('Error al guardar el viaje');
    }
  };

  const handleDeleteShipTrip = async (shipTrip: ShipTrip) => {
    // Check if trip has settlements
    if (shipTrip.settlements_count?.[0]?.count > 0) {
      toast.error('No se puede eliminar un viaje con liquidaciones asociadas');
      return;
    }

    if (await confirm({ title: '¿Eliminar este viaje?', isDestructive: true })) {
      try {
        await deleteShipTrip(shipTrip.id);
        toast.success('Viaje eliminado correctamente');
        fetchData();
      } catch (error) {
        console.error('Error deleting ship trip:', error);
        toast.error('Error al eliminar el viaje');
      }
    }
  };

  const handleMergeShips = async () => {
    if (selectedShips.length < 2) {
      toast.error('Seleccione al menos 2 naves para unir');
      return;
    }

    try {
      // Create new ship
      const newShip = await createShip({
        name: newMergedShipName,
        company: ships.find((s) => s.id === selectedShips[0])?.company || '',
        type: ships.find((s) => s.id === selectedShips[0])?.type || '',
      });

      console.log('newShip', newShip);

      // Update all ship trips from selected ships to point to the new ship
      for (const shipId of selectedShips) {
        const tripsToUpdate = shipTrips.filter(
          (trip) => trip.ship_id === shipId
        );
        for (const trip of tripsToUpdate) {
          await updateShipTrip(trip.id, {
            ship_id: newShip.id,
            etd: trip.etd,
            eta: trip.eta,
          });
        }
        // Delete old ship
        // await deleteShip(shipId);
      }

      // Update local state
      const updatedTrips = shipTrips.map((trip) => {
        if (selectedShips.includes(trip.ship_id)) {
          return {
            ...trip,
            ship_id: newShip.id,
            ship: {
              ...trip.ship,
              id: newShip.id,
              name: newMergedShipName,
            },
          };
        }
        return trip;
      });

      setShipTrips(updatedTrips);

      toast.success('Naves unidas correctamente');
      setMergeMode(false);
      setSelectedShips([]);
      setMergeModalOpen(false);
      setNewMergedShipName('');
      fetchData();
    } catch (error) {
      console.error('Error merging ships:', error);
      toast.error('Error al unir las naves');
    }
  };

  const handleShipSelection = (shipId: number, checked: boolean) => {
    console.log('Selection attempt:', { shipId, checked }); // Debug log
    setSelectedShips((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, shipId]
        : prevSelected.filter((id) => id !== shipId);
      console.log('New selection:', newSelected); // Debug log
      return newSelected;
    });
  };

  const handleCreateShipTrip = async () => {
    try {
      if (!editingShip) return;

      const newTrip = await createShipTrip({
        ship_id: editingShip.id,
        voyage_number: newShipTripData.voyage_number,
        origin_market_id: newShipTripData.origin_market_id,
        etd: new Date(newShipTripData.etd).toISOString(),
        eta: new Date(newShipTripData.eta).toISOString(),
        market_id: newShipTripData.market_id,
      });

      // Add ship reference to the new trip
      const newTripWithShip = {
        ...newTrip,
        ship: editingShip,
        market: markets.find((m) => m.id == newShipTripData.market_id),
        settlements_count: [{ count: 0 }],
      };

      // Update shipTrips state
      setShipTrips((prev) => [...prev, newTripWithShip]);

      // Update ships state
      setShips((prev) =>
        prev.map((ship) => {
          if (ship.id === editingShip.id) {
            return {
              ...ship,
              ship_trips: [...(ship.ship_trips || []), newTripWithShip],
            };
          }
          return ship;
        })
      );

      // Update editingShip state to reflect the new trip
      setEditingShip((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          ship_trips: [...(prev.ship_trips || []), newTripWithShip],
        };
      });

      toast.success('Viaje creado correctamente');
      newShipTripModal.onClose();
      setNewShipTripData({
        voyage_number: '',
        origin_market_id: '',
        etd: '',
        eta: '',
        market_id: '',
      }); // Reset form
    } catch (error) {
      console.error('Error creating ship trip:', error);
      toast.error('Error al crear el viaje');
    }
  };

  const handleTripSelection = (tripId: number, checked: boolean) => {
    setSelectedTrips((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, tripId]
        : prevSelected.filter((id) => id !== tripId);

      // If this is the first selection, set the initial dates
      if (checked && newSelected.length === 1) {
        const selectedTrip = shipTrips.find((t) => t.id === tripId);
        if (selectedTrip) {
          setNewMergedTripDates({
            etd: selectedTrip.etd.split('T')[0],
            eta: selectedTrip.eta.split('T')[0],
          });
        }
      }

      return newSelected;
    });
  };

  const handleMergeTrips = async () => {
    if (selectedTrips.length < 2) {
      toast.error('Seleccione al menos 2 viajes para unir');
      return;
    }

    if (!newMergedTripDates.market_id) {
      toast.error('Seleccione un mercado');
      return;
    }

    // Check if all selected trips belong to the same ship
    const firstTrip = shipTrips.find((trip) => trip.id === selectedTrips[0]);
    const allSameShip = selectedTrips.every((tripId) => {
      const trip = shipTrips.find((t) => t.id === tripId);
      return trip?.ship_id === firstTrip?.ship_id;
    });

    if (!allSameShip) {
      toast.error('Solo se pueden unir viajes de la misma nave');
      return;
    }

    try {
      console.log('Selected trips for merging:', selectedTrips);

      // Create new trip
      const newTrip = await createShipTrip({
        ship_id: firstTrip!.ship_id,
        etd: new Date(newMergedTripDates.etd).toISOString(),
        eta: new Date(newMergedTripDates.eta).toISOString(),
        market_id: newMergedTripDates.market_id,
      });

      console.log('Created new trip:', newTrip);

      // Update settlements for each old trip
      for (const tripId of selectedTrips) {
        console.log(
          `Updating settlements for trip ${tripId} to new trip ${newTrip.id}`
        );
        try {
          await updateSettlementShipTripByShipTripId(tripId, newTrip.id);
        } catch (error) {
          console.error(
            `Error updating settlements for trip ${tripId}:`,
            error
          );
          throw error;
        }
      }

      toast.success('Viajes unidos correctamente');
      setTripMergeMode(false);
      setSelectedTrips([]);
      setTripMergeModalOpen(false);
      setNewMergedTripDates({ etd: '', eta: '' });
      await fetchData();
    } catch (error) {
      console.error('Error merging trips:', error);
      toast.error('Error al unir los viajes');
    }
  };

  const handleTransferSettlements = async () => {
    if (!originTripId || !destinationTripId) {
      toast.error('Seleccione viajes de origen y destino');
      return;
    }

    try {
      await updateSettlementShipTripByShipTripId(
        originTripId,
        destinationTripId
      );
      toast.success('Liquidaciones transferidas correctamente');
      setTransferModalOpen(false);
      setOriginTripId(null);
      setDestinationTripId(null);
      fetchData();
    } catch (error) {
      console.error('Error transferring settlements:', error);
      toast.error('Error al transferir las liquidaciones');
    }
  };

  const shipColumns = [
    { name: 'NOMBRE', uid: 'name' },
    { name: 'COMPAÑÍA', uid: 'company' },
    { name: 'TIPO', uid: 'type' },
    { name: 'VIAJES', uid: 'trips_count' },
    { name: 'ACCIONES', uid: 'actions' },
  ];

  const shipTripsColumns = [
    { name: 'NAVE', uid: 'ship' },
    { name: 'VOYAGE NUMBER', uid: 'voyage_number' },
    { name: 'ETD', uid: 'etd' },
    { name: 'ETA', uid: 'eta' },
    { name: 'MERCADO', uid: 'market' },
    { name: 'ORIGEN', uid: 'origin_market' },
    { name: 'LIQUIDACIONES', uid: 'settlements_count' },
    { name: 'ACCIONES', uid: 'actions' },
  ];

  // Helpers for calendar rendering
  const startOfMonth = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    return d;
  };
  const endOfMonth = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return d;
  };
  const toYMD = (date: Date) => {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const stripTime = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const parseISODate = (iso: string) => {
    const d = new Date(iso);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  const isDateInRange = (day: Date, start: Date, end: Date) => {
    const ds = stripTime(day).getTime();
    const s = stripTime(start).getTime();
    const e = stripTime(end).getTime();
    return ds >= s && ds <= e;
  };
  const getMonthDays = (date: Date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    // Monday-start calendar: convert JS Sunday(0) to 7
    const startWeekday = (start.getDay() || 7) as number;
    const days: { date: Date; inMonth: boolean }[] = [];
    // leading days (previous month)
    for (let i = 1; i < startWeekday; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() - (startWeekday - i));
      days.push({ date: d, inMonth: false });
    }
    // current month
    for (let d = 1; d <= end.getDate(); d++) {
      days.push({
        date: new Date(date.getFullYear(), date.getMonth(), d),
        inMonth: true,
      });
    }
    // trailing days to complete 6 weeks (42 cells)
    while (days.length % 7 !== 0) {
      const last = days[days.length - 1].date;
      const d = new Date(last);
      d.setDate(last.getDate() + 1);
      days.push({ date: d, inMonth: false });
    }
    // ensure 6 rows
    while (days.length < 42) {
      const last = days[days.length - 1].date;
      const d = new Date(last);
      d.setDate(last.getDate() + 1);
      days.push({ date: d, inMonth: false });
    }
    return days;
  };
  const marketOptions = useMemo(
    () =>
      markets.map((market) => ({
        key: market.id,
        label: `${market.id} - ${market.name} - ${market.country}`,
        value: market.id,
      })),
    [markets]
  );

  const bulkUploadStats = useMemo(
    () => ({
      shipsToCreate: new Set(
        bulkUploadPreviewRows
          .filter(
            (row) =>
              row.shipAction === 'create' &&
              selectedBulkTripRows.has(row.id) &&
              row.tripAction === 'create'
          )
          .map((row) => `${row.shipType}:${normalizeShipName(row.shipName)}`)
      ).size,
      tripsToCreate: bulkUploadPreviewRows.filter(
        (row) => row.tripAction === 'create' && selectedBulkTripRows.has(row.id)
      ).length,
      existingTrips: bulkUploadPreviewRows.filter(
        (row) => row.tripAction === 'reuse'
      ).length,
      duplicateRows: bulkUploadPreviewRows.filter(
        (row) => row.tripAction === 'duplicate-file'
      ).length,
      blockingErrors:
        bulkUploadErrors.length +
        bulkUploadPreviewRows.filter(
          (row) =>
            row.shipAction === 'error' ||
            row.tripAction === 'error' ||
            row.errors.length > 0
        ).length,
    }),
    [bulkUploadErrors, bulkUploadPreviewRows, selectedBulkTripRows]
  );

  const missingMarketLocodes = useMemo(() => {
    const existingLocodes = new Set(markets.map((market) => normalizeLocode(market.id)));
    const missing = new Set<string>();

    bulkUploadRows.forEach((row) => {
      if (row.originLocode && !existingLocodes.has(row.originLocode)) {
        missing.add(row.originLocode);
      }
      if (
        row.destinationLocode &&
        !existingLocodes.has(row.destinationLocode)
      ) {
        missing.add(row.destinationLocode);
      }
    });

    return Array.from(missing).sort();
  }, [bulkUploadRows, markets]);

  const resetBulkUpload = () => {
    setBulkUploadFileName('');
    setBulkUploadRows([]);
    setBulkUploadPreviewRows([]);
    setSelectedBulkTripRows(new Set());
    setMissingMarketDrafts([]);
    setBulkUploadErrors([]);
    setIsParsingBulkUpload(false);
    setIsConfirmingBulkUpload(false);
    setIsSuggestingMarkets(false);
    setIsCreatingMarkets(false);
  };

  const buildBulkUploadPreview = (
    parsedRows: BulkShipUploadRow[],
    marketsSource: Market[] = markets
  ): BulkShipUploadPreviewRow[] => {
    const marketsByLocode = new Map(
      marketsSource.map((market) => [normalizeLocode(market.id), market])
    );
    const plannedUploadTrips: {
      shipKey: string;
      originLocode: string;
      destinationLocode: string;
      etd: string;
      eta: string;
      rowNumber: number;
    }[] = [];

    return parsedRows.map((row) => {
      const errors = [...row.errors];
      const warnings = [...row.warnings];
      const shipCandidates = ships
        .filter((ship) => !row.shipType || ship.type === row.shipType)
        .map((ship) => ({
          ship,
          score: similarityScore(ship.name, row.shipName),
        }))
        .sort((a, b) => b.score - a.score);
      const bestShipMatch = shipCandidates[0];
      const matchedShip =
        bestShipMatch && bestShipMatch.score >= 0.82
          ? bestShipMatch.ship
          : undefined;

      if (bestShipMatch && !matchedShip && bestShipMatch.score >= 0.68) {
        warnings.push(
          `Posible nave existente: ${bestShipMatch.ship.name} (${Math.round(
            bestShipMatch.score * 100
          )}%)`
        );
      }

      const originMarket = marketsByLocode.get(row.originLocode);
      const destinationMarket = marketsByLocode.get(row.destinationLocode);
      const shipAction = errors.length > 0
        ? 'error'
        : matchedShip
          ? 'reuse'
          : 'create';

      let tripAction: BulkShipUploadPreviewRow['tripAction'] =
        errors.length > 0 ? 'error' : 'create';
      let matchedTrip: ShipTrip | undefined;
      let duplicateOfRow: number | undefined;

      if (!originMarket) {
        errors.push(`ORIGEN LOCODE no existe: ${row.originLocode || '-'}`);
      }
      if (!destinationMarket) {
        errors.push(
          `DESTINO LOCODE no existe: ${row.destinationLocode || '-'}`
        );
      }

      if (
        row.etd &&
        row.eta &&
        new Date(`${row.etd}T12:00:00`) > new Date(`${row.eta}T12:00:00`)
      ) {
        errors.push('ETD no puede ser posterior a ETA');
      }

      const resolvedShipKey = matchedShip
        ? `existing:${matchedShip.id}`
        : `new:${row.shipType}:${normalizeShipName(row.shipName)}`;

      if (errors.length === 0) {
        if (matchedShip && originMarket && destinationMarket && row.etd && row.eta) {
          matchedTrip = shipTrips.find((trip) => {
            const tripEtd = trip.etd?.split('T')[0];
            const tripEta = trip.eta?.split('T')[0];
            return (
              trip.ship_id === matchedShip.id &&
              normalizeLocode(trip.origin_market_id) ===
                normalizeLocode(originMarket.id) &&
              normalizeLocode(trip.market_id) ===
                normalizeLocode(destinationMarket.id) &&
              tripEtd &&
              tripEta &&
              isYMDWithinDays(tripEtd, row.etd!, 7) &&
              isYMDWithinDays(tripEta, row.eta!, 7)
            );
          });
        }

        if (matchedTrip) {
          tripAction = 'reuse';
        } else {
          const duplicateUploadTrip = plannedUploadTrips.find(
            (trip) =>
              trip.shipKey === resolvedShipKey &&
              trip.originLocode === normalizeLocode(originMarket?.id) &&
              trip.destinationLocode === normalizeLocode(destinationMarket?.id) &&
              isYMDWithinDays(trip.etd, row.etd!, 7) &&
              isYMDWithinDays(trip.eta, row.eta!, 7)
          );
          if (duplicateUploadTrip) {
            duplicateOfRow = duplicateUploadTrip.rowNumber;
            tripAction = 'duplicate-file';
          } else {
            plannedUploadTrips.push({
              shipKey: resolvedShipKey,
              originLocode: normalizeLocode(originMarket?.id),
              destinationLocode: normalizeLocode(destinationMarket?.id),
              etd: row.etd!,
              eta: row.eta!,
              rowNumber: row.rowNumber,
            });
            tripAction = 'create';
          }
        }
      } else {
        tripAction = 'error';
      }

      return {
        ...row,
        id: `${row.rowNumber}-${row.shipName}-${row.etd}-${row.eta}`,
        errors,
        warnings,
        matchedShip,
        shipMatchScore: bestShipMatch?.score,
        shipAction,
        matchedTrip,
        tripAction,
        duplicateOfRow,
      };
    });
  };

  const initializeMissingMarketDrafts = (
    parsedRows: BulkShipUploadRow[],
    marketsSource: Market[] = markets
  ) => {
    const existingLocodes = new Set(
      marketsSource.map((market) => normalizeLocode(market.id))
    );
    const missing = new Set<string>();

    parsedRows.forEach((row) => {
      if (row.originLocode && !existingLocodes.has(row.originLocode)) {
        missing.add(row.originLocode);
      }
      if (
        row.destinationLocode &&
        !existingLocodes.has(row.destinationLocode)
      ) {
        missing.add(row.destinationLocode);
      }
    });

    setMissingMarketDrafts(
      Array.from(missing)
        .sort()
        .map((locode) => ({
          id: locode,
          name: '',
          country: getCountryFromLocode(locode),
          country_code: getCountryCodeFromLocode(locode),
          primary_market_id: null,
          reviewed: false,
        }))
    );
  };

  const handleBulkUploadFile = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error('Seleccione un archivo Excel válido (.xlsx o .xls)');
      return;
    }

    setIsParsingBulkUpload(true);
    setBulkUploadFileName(file.name);
    setBulkUploadPreviewRows([]);
    setBulkUploadErrors([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const sheetName = workbook.SheetNames[0];
      const worksheet = sheetName ? workbook.Sheets[sheetName] : undefined;

      if (!worksheet) {
        setBulkUploadErrors(['El archivo no tiene hojas para procesar']);
        return;
      }

      const rows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
        header: 1,
        defval: '',
        raw: true,
      });
      const headerRow = rows[0] || [];
      const headerIndex = new Map<string, number>();
      headerRow.forEach((header, index) => {
        const normalized = normalizeHeader(header);
        if (normalized) headerIndex.set(normalized, index);
      });

      const missingHeaders = BULK_UPLOAD_HEADERS.filter(
        (header) => !headerIndex.has(normalizeHeader(header))
      );
      if (missingHeaders.length > 0) {
        setBulkUploadErrors([
          `Faltan columnas requeridas: ${missingHeaders.join(', ')}`,
        ]);
        return;
      }

      const getCell = (row: unknown[], header: string) =>
        row[headerIndex.get(normalizeHeader(header)) ?? -1];

      const parsedRows = rows
        .slice(1)
        .map((excelRow, index): BulkShipUploadRow | null => {
          const values = BULK_UPLOAD_HEADERS.map((header) =>
            getCell(excelRow, header)
          );
          if (
            values.every((value) => String(value ?? '').trim().length === 0)
          ) {
            return null;
          }

          const shipName = String(getCell(excelRow, 'Nave') ?? '').trim();
          const shipType = parseShipType(getCell(excelRow, 'Tipo'));
          const etd = parseBulkUploadDate(getCell(excelRow, 'ETD'));
          const eta = parseBulkUploadDate(getCell(excelRow, 'ETA'));
          const originLocode = normalizeLocode(
            getCell(excelRow, 'ORIGEN LOCODE')
          );
          const destinationLocode = normalizeLocode(
            getCell(excelRow, 'DESTINO LOCODE')
          );
          const errors: string[] = [];

          if (!shipName) errors.push('Nave es requerida');
          if (!shipType) errors.push('Tipo inválido o requerido');
          if (!etd) errors.push('ETD inválido o requerido');
          if (!eta) errors.push('ETA inválido o requerido');
          if (!originLocode) errors.push('ORIGEN LOCODE es requerido');
          if (!destinationLocode) errors.push('DESTINO LOCODE es requerido');

          return {
            rowNumber: index + 2,
            shipName,
            shipType,
            etd,
            eta,
            originLocode,
            destinationLocode,
            errors,
            warnings: [],
          };
        })
        .filter((row): row is BulkShipUploadRow => Boolean(row));

      if (parsedRows.length === 0) {
        setBulkUploadErrors(['El archivo no contiene filas para importar']);
        return;
      }

      setBulkUploadRows(parsedRows);
      initializeMissingMarketDrafts(parsedRows);
      const previewRows = buildBulkUploadPreview(parsedRows);
      setBulkUploadPreviewRows(previewRows);
      setSelectedBulkTripRows(
        new Set(
          previewRows
            .filter((row) => row.tripAction === 'create')
            .map((row) => row.id)
        )
      );
    } catch (error) {
      console.error('Error parsing ships upload:', error);
      setBulkUploadErrors(['No se pudo leer el archivo Excel']);
    } finally {
      setIsParsingBulkUpload(false);
    }
  };

  const updateMissingMarketDraft = (
    locode: string,
    updates: Partial<MissingMarketDraft>
  ) => {
    setMissingMarketDrafts((drafts) =>
      drafts.map((draft) =>
        draft.id === locode ? { ...draft, ...updates } : draft
      )
    );
  };

  const handleSuggestMissingMarkets = async () => {
    if (missingMarketLocodes.length === 0 || isSuggestingMarkets) return;

    setIsSuggestingMarkets(true);
    try {
      const suggestions = await suggestMarkets(missingMarketLocodes);
      setMissingMarketDrafts((drafts) =>
        drafts.map((draft) => {
          const suggestion = suggestions.find(
            (market) => normalizeLocode(market.id) === draft.id
          );
          if (!suggestion) return draft;
          return {
            ...draft,
            name: suggestion.name || draft.name,
            country: suggestion.country || draft.country,
            country_code:
              suggestion.country_code || getCountryCodeFromLocode(draft.id),
          };
        })
      );
      toast.success('Sugerencias generadas');
    } catch (error) {
      console.error('Error suggesting markets:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'No se pudieron generar sugerencias'
      );
    } finally {
      setIsSuggestingMarkets(false);
    }
  };

  const handleCreateMissingMarkets = async () => {
    if (missingMarketDrafts.length === 0 || isCreatingMarkets) return;

    const incompleteDraft = missingMarketDrafts.find(
      (draft) => !draft.id || !draft.name.trim() || !draft.country.trim()
    );
    if (incompleteDraft) {
      toast.error(`Complete nombre y país para ${incompleteDraft.id}`);
      return;
    }

    setIsCreatingMarkets(true);
    try {
      for (const draft of missingMarketDrafts) {
        await createMarket({
          id: draft.id,
          name: draft.name.trim().toUpperCase(),
          country: draft.country.trim().toUpperCase(),
          country_code: draft.country_code || getCountryCodeFromLocode(draft.id),
          primary_market_id: draft.primary_market_id,
          reviewed: draft.reviewed,
        });
      }

      const updatedMarkets = await getMarkets();
      const nextPreviewRows = buildBulkUploadPreview(
        bulkUploadRows,
        updatedMarkets
      );
      setMarkets(updatedMarkets);
      setMissingMarketDrafts([]);
      setBulkUploadPreviewRows(nextPreviewRows);
      setSelectedBulkTripRows(
        new Set(
          nextPreviewRows
            .filter((row) => row.tripAction === 'create')
            .map((row) => row.id)
        )
      );
      toast.success('Mercados creados correctamente');
    } catch (error) {
      console.error('Error creating missing markets:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'No se pudieron crear los mercados'
      );
    } finally {
      setIsCreatingMarkets(false);
    }
  };

  const toggleBulkTripRow = (rowId: string, selected: boolean) => {
    setSelectedBulkTripRows((current) => {
      const next = new Set(current);
      if (selected) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  const setAllCreateTripRowsSelected = (selected: boolean) => {
    setSelectedBulkTripRows(
      selected
        ? new Set(
            bulkUploadPreviewRows
              .filter((row) => row.tripAction === 'create')
              .map((row) => row.id)
          )
        : new Set()
    );
  };

  const handleConfirmBulkUpload = async () => {
    if (
      bulkUploadStats.blockingErrors > 0 ||
      missingMarketLocodes.length > 0 ||
      isConfirmingBulkUpload
    ) {
      return;
    }

    setIsConfirmingBulkUpload(true);
    try {
      const createdShipsByKey = new Map<string, Ship>();
      let shipsCreated = 0;
      let tripsCreated = 0;
      const rowsToCreate = bulkUploadPreviewRows.filter(
        (row) => row.tripAction === 'create' && selectedBulkTripRows.has(row.id)
      );

      for (const row of rowsToCreate) {
        if (row.shipAction !== 'create' || !row.shipType) continue;

        const shipKey = `${row.shipType}:${normalizeShipName(row.shipName)}`;
        if (createdShipsByKey.has(shipKey)) continue;

        const createdShip = await createShip({
          name: row.shipName,
          company: '',
          type: row.shipType,
        });

        if (!createdShip) {
          throw new Error(`No se pudo crear la nave ${row.shipName}`);
        }

        createdShipsByKey.set(shipKey, createdShip);
        shipsCreated += 1;
      }

      for (const row of rowsToCreate) {
        if (!row.shipType) continue;
        const ship =
          row.matchedShip ||
          createdShipsByKey.get(`${row.shipType}:${normalizeShipName(row.shipName)}`);

        if (!ship || !row.etd || !row.eta) {
          throw new Error(`No se pudo resolver la nave para fila ${row.rowNumber}`);
        }

        const createdTrip = await createShipTrip({
          ship_id: ship.id,
          origin_market_id: row.originLocode,
          market_id: row.destinationLocode,
          etd: new Date(`${row.etd}T12:00:00`).toISOString(),
          eta: new Date(`${row.eta}T12:00:00`).toISOString(),
        });

        if (!createdTrip) {
          throw new Error(`No se pudo crear el viaje de fila ${row.rowNumber}`);
        }

        tripsCreated += 1;
      }

      toast.success(
        `Importación lista: ${shipsCreated} naves y ${tripsCreated} viajes creados`
      );
      resetBulkUpload();
      bulkUploadModal.onClose();
      await fetchData();
    } catch (error) {
      console.error('Error confirming ships upload:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Error al confirmar la importación'
      );
    } finally {
      setIsConfirmingBulkUpload(false);
    }
  };

  const monthDays = useMemo(() => getMonthDays(calendarDate), [calendarDate]);
  const weekdayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const monthPureDays = useMemo(() => {
    const start = startOfMonth(calendarDate);
    const end = endOfMonth(calendarDate);
    return Array.from(
      { length: end.getDate() },
      (_, i) => new Date(start.getFullYear(), start.getMonth(), i + 1)
    );
  }, [calendarDate]);

  const calendarTripsByDay = useMemo(() => {
    const map: Record<string, ShipTrip[]> = {};
    let filtered = calendarShipFilter
      ? shipTrips.filter((t) => `${t.ship_id}` === calendarShipFilter)
      : shipTrips;
    if (shipTypeFilter) {
      filtered = filtered.filter((t) => t.ship?.type === shipTypeFilter);
    }
    for (const t of filtered) {
      if (!t.etd || !t.eta) continue;
      const start = parseISODate(t.etd);
      const end = parseISODate(t.eta);
      for (const cell of monthDays) {
        if (!cell.inMonth) continue; // only list within current month cells
        if (isDateInRange(cell.date, start, end)) {
          const key = toYMD(cell.date);
          if (!map[key]) map[key] = [];
          map[key].push(t);
        }
      }
    }
    return map;
  }, [shipTrips, monthDays, calendarShipFilter, shipTypeFilter]);

  const resourceTimelineShips = useMemo(() => {
    const monthStart = startOfMonth(calendarDate);
    const monthEnd = endOfMonth(calendarDate);

    const shipsWithTrips = ships
      .filter((s) => !shipTypeFilter || s.type === shipTypeFilter)
      .filter(
        (s) =>
          !resourceSearchTerm ||
          s.name.toLowerCase().includes(resourceSearchTerm.toLowerCase())
      )
      .map((s) => {
        const trips = shipTrips
          .filter((t) => t.ship_id === s.id)
          .filter((t): t is ShipTrip & { etd: string; eta: string } => {
            if (!t.etd || !t.eta) return false;
            const sD = parseISODate(t.etd);
            const eD = parseISODate(t.eta);
            // overlaps month
            return eD >= monthStart && sD <= monthEnd;
          })
          .sort(
            (a, b) => new Date(a.etd).getTime() - new Date(b.etd).getTime()
          );

        return { ship: s, trips };
      })
      .filter(({ trips }) => trips.length > 0);

    return shipsWithTrips.sort((a, b) => {
      if (resourceSort === 'etd') {
        return (
          new Date(a.trips[0].etd).getTime() -
          new Date(b.trips[0].etd).getTime()
        );
      }
      if (resourceSort === 'eta') {
        return (
          new Date(a.trips[0].eta).getTime() -
          new Date(b.trips[0].eta).getTime()
        );
      }
      return a.ship.name.localeCompare(b.ship.name);
    });
  }, [
    ships,
    shipTrips,
    shipTypeFilter,
    resourceSearchTerm,
    calendarDate,
    resourceSort,
  ]);

  const monthStart = startOfMonth(calendarDate);
  const monthEnd = endOfMonth(calendarDate);
  const numDays = monthPureDays.length;

  const renderCell = (item: Ship | ShipTrip, columnKey: string) => {
    if (selectedTab === 'ships') {
      const ship = item as Ship;
      switch (columnKey) {
        case 'name':
          return (
            <div className='flex items-center gap-2'>
              {mergeMode && (
                <input
                  type='checkbox'
                  checked={selectedShips.includes(ship.id)}
                  onChange={(e) =>
                    handleShipSelection(ship.id, e.target.checked)
                  }
                  className='w-4 h-4 cursor-pointer'
                />
              )}
              <span>{ship.name}</span>
            </div>
          );
        case 'company':
          return ship.company || '-';
        case 'type':
          return ship.type || '-';
        case 'trips_count':
          return ship.ship_trips?.length || 0;
        case 'actions':
          return (
            <div className='flex gap-2'>
              {!mergeMode && (
                <>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    onPress={() => handleEditShip(ship)}
                  >
                    <Icon icon='tabler:edit' className='text-default-400' />
                  </Button>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='light'
                    color='danger'
                    onPress={() => handleDeleteShip(ship.id)}
                    isDisabled={ship.ship_trips?.length > 0}
                  >
                    <Icon icon='tabler:trash' className='text-danger' />
                  </Button>
                </>
              )}
            </div>
          );
        default:
          return null;
      }
    } else {
      const shipTrip = item as ShipTrip;
      switch (columnKey) {
        case 'ship':
          return (
            <div className='flex items-center gap-2'>
              {tripMergeMode && (
                <input
                  type='checkbox'
                  checked={selectedTrips.includes(shipTrip.id)}
                  onChange={(e) =>
                    handleTripSelection(shipTrip.id, e.target.checked)
                  }
                  disabled={
                    selectedTrips.length > 0 &&
                    !selectedTrips.includes(shipTrip.id) &&
                    shipTrip.ship_id !==
                      shipTrips.find((t) => t.id === selectedTrips[0])?.ship_id
                  }
                  className='w-4 h-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                />
              )}
              <span>{shipTrip.ship?.name || '-'}</span>
            </div>
          );
        case 'etd':
          return shipTrip.etd ? shipTrip.etd.split('T')[0] : '-';
        case 'eta':
          return shipTrip.eta ? shipTrip.eta.split('T')[0] : '-';

        case 'market':
          return shipTrip.market
            ? `${shipTrip.market.id} - ${shipTrip.market.name} - ${shipTrip.market.country}`
            : '-';
        case 'origin_market':
          return shipTrip.origin_market
            ? `${shipTrip.origin_market.id} - ${shipTrip.origin_market.name} - ${shipTrip.origin_market.country}`
            : '-';
        case 'voyage_number':
          return shipTrip.voyage_number ? shipTrip.voyage_number : '-';
        case 'settlements_count':
          return shipTrip.settlements_count?.[0]?.count || 0;
        case 'actions':
          return (
            <div className='flex gap-2'>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={() => handleEditShipTrip(shipTrip)}
              >
                <Icon icon='tabler:edit' className='text-default-400' />
              </Button>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                color='danger'
                onPress={() => handleDeleteShipTrip(shipTrip)}
                isDisabled={shipTrip.settlements_count[0].count > 0}
              >
                <Icon icon='tabler:trash' className='text-danger' />
              </Button>
              {shipTrip.settlements_count[0].count > 0 && (
                <Button
                  size='sm'
                  variant='light'
                  color='primary'
                  onPress={() => {
                    setOriginTripId(shipTrip.id);
                    setTransferModalOpen(true);
                  }}
                >
                  Transferir
                </Button>
              )}
            </div>
          );
        default:
          return null;
      }
    }
  };

  return (
    <PageLayout>
      <Header
        title='Gestión de Naves'
        actions={
          <Button
            variant='flat'
            startContent={<Icon icon='tabler:upload' />}
            isDisabled={loading}
            onPress={() => {
              resetBulkUpload();
              bulkUploadModal.onOpen();
            }}
          >
            Importar planilla
          </Button>
        }
      />

      <Tabs
        selectedKey={selectedTab}
        onSelectionChange={(key) => setSelectedTab(key.toString())}
      >
        <Tab key='ships' title='Naves'>
          <div className='flex justify-between items-center mb-6 gap-3'>
            <div className='flex items-center gap-3'>
              <Input
                placeholder='Buscar nave...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                startContent={<Icon icon='tabler:search' className='text-default-300' />}
                className='w-72'
              />
              <Select
                label='Tipo'
                className='min-w-[160px]'
                selectedKeys={shipTypeFilter ? [shipTypeFilter] : []}
                onChange={(e) => setShipTypeFilter(e.target.value)}
              >
                <SelectItem key='' value=''>
                  Todos
                </SelectItem>
                <SelectItem key='A' value='A'>
                  Aéreo
                </SelectItem>
                <SelectItem key='M' value='M'>
                  Marítimo
                </SelectItem>
                <SelectItem key='T' value='T'>
                  Terrestre
                </SelectItem>
              </Select>
            </div>
            <div className='flex gap-2'>
              {mergeMode ? (
                <>
                  <Button
                    variant='light'
                    onPress={() => {
                      console.log('Canceling merge mode');
                      setMergeMode(false);
                      setSelectedShips([]);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    color='primary'
                    onPress={() => {
                      console.log('Confirming merge');
                      if (selectedShips.length < 2) {
                        toast.error('Seleccione al menos 2 naves para unir');
                        return;
                      }
                      setMergeModalOpen(true);
                    }}
                    isDisabled={selectedShips.length < 2}
                  >
                    Confirmar
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    color='primary'
                    onPress={() => {
                      setEditingShip(null);
                      setNewShipData({ name: '', company: '', type: '' });
                      onOpen();
                    }}
                    startContent={<Icon icon='tabler:plus' />}
                  >
                    Nueva Nave
                  </Button>
                  <Button
                    variant='flat'
                    onPress={() => {
                      console.log('Activating merge mode');
                      setMergeMode((prev) => {
                        console.log('Setting merge mode to:', !prev);
                        return !prev;
                      });
                    }}
                  >
                    Unir Naves
                  </Button>
                </>
              )}
            </div>
          </div>

          <Card>
            {mergeMode && (
              <Alert className='m-4'>
                Modo de unión activo. Seleccione las naves que desea unir.
              </Alert>
            )}
            <Table
              aria-label='Tabla de Naves'
              key={`${mergeMode}-${selectedShips.length}`}
            >
              <TableHeader columns={shipColumns}>
                {(column) => (
                  <TableColumn key={column.uid}>{column.name}</TableColumn>
                )}
              </TableHeader>
              <TableBody items={filteredShips} emptyContent='No hay naves'>
                {(item) => (
                  <TableRow
                    key={`${item.id}-${selectedShips.includes(item.id)}`}
                  >
                    {(columnKey) => (
                      <TableCell>{renderCell(item, columnKey)}</TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Tab>

        <Tab key='trips' title='Viajes'>
          <div className='flex justify-between items-center mb-6 gap-3'>
            <div className='flex items-center gap-3'>
              <Input
                placeholder='Buscar viaje...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                startContent={<Icon icon='tabler:search' className='text-default-300' />}
                className='w-72'
              />
              <Select
                label='Tipo de nave'
                className='min-w-[180px]'
                selectedKeys={shipTypeFilter ? [shipTypeFilter] : []}
                onChange={(e) => setShipTypeFilter(e.target.value)}
              >
                <SelectItem key='' value=''>
                  Todos
                </SelectItem>
                <SelectItem key='A' value='A'>
                  Aéreo
                </SelectItem>
                <SelectItem key='M' value='M'>
                  Marítimo
                </SelectItem>
                <SelectItem key='T' value='T'>
                  Terrestre
                </SelectItem>
              </Select>
            </div>
            <div className='flex gap-2'>
              {tripMergeMode ? (
                <>
                  <Button
                    variant='light'
                    onPress={() => {
                      setTripMergeMode(false);
                      setSelectedTrips([]);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    color='primary'
                    onPress={() => {
                      if (selectedTrips.length < 2) {
                        toast.error('Seleccione al menos 2 viajes para unir');
                        return;
                      }
                      setTripMergeModalOpen(true);
                    }}
                    isDisabled={selectedTrips.length < 2}
                  >
                    Confirmar
                  </Button>
                </>
              ) : (
                <Button
                  variant='flat'
                  onPress={() => setTripMergeMode(true)}
                >
                  Unir Viajes
                </Button>
              )}
            </div>
          </div>

          <Card>
            {tripMergeMode && (
              <Alert className='m-4'>
                Modo de unión activo. Seleccione los viajes que desea unir.
              </Alert>
            )}
            <Table
              aria-label='Tabla de Viajes'
              key={`${tripMergeMode}-${selectedTrips.length}`}
            >
              <TableHeader columns={shipTripsColumns}>
                {(column) => (
                  <TableColumn key={column.uid}>{column.name}</TableColumn>
                )}
              </TableHeader>
              <TableBody items={filteredShipTrips} emptyContent='No hay viajes'>
                {(item) => (
                  <TableRow
                    key={`${item.id}-${selectedTrips.includes(item.id)}`}
                  >
                    {(columnKey) => (
                      <TableCell>{renderCell(item, columnKey)}</TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </Tab>

        <Tab key='calendar' title='Calendario'>
          <div className='flex flex-col gap-4 mb-6'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='flex items-center gap-2'>
                <Button
                  variant='light'
                  onPress={() =>
                    setCalendarDate(
                      (d) => new Date(d.getFullYear(), d.getMonth() - 1, 1)
                    )
                  }
                >
                  Mes anterior
                </Button>
                <div className='font-semibold'>
                  {calendarDate.toLocaleString('es-ES', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
                <Button
                  variant='light'
                  onPress={() =>
                    setCalendarDate(
                      (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1)
                    )
                  }
                >
                  Mes siguiente
                </Button>
                <Button
                  variant='flat'
                  onPress={() => setCalendarDate(new Date())}
                >
                  Hoy
                </Button>
              </div>
              <div className='min-w-[220px]'>
                <Select
                  label='Filtrar por nave'
                  selectedKeys={calendarShipFilter ? [calendarShipFilter] : []}
                  onChange={(e) => setCalendarShipFilter(e.target.value)}
                >
                  <SelectItem key='' value=''>
                    Todas
                  </SelectItem>
                  {ships.map((s) => (
                    <SelectItem key={String(s.id)} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              <div className='min-w-[200px]'>
                <Select
                  label='Tipo de nave'
                  selectedKeys={shipTypeFilter ? [shipTypeFilter] : []}
                  onChange={(e) => setShipTypeFilter(e.target.value)}
                >
                  <SelectItem key='' value=''>
                    Todos
                  </SelectItem>
                  <SelectItem key='A' value='A'>
                    Aéreo
                  </SelectItem>
                  <SelectItem key='M' value='M'>
                    Marítimo
                  </SelectItem>
                  <SelectItem key='T' value='T'>
                    Terrestre
                  </SelectItem>
                </Select>
              </div>
            </div>

            <Card className='p-4'>
              <div className='grid grid-cols-7 gap-2 mb-2'>
                {weekdayLabels.map((w) => (
                  <div key={w} className='text-sm font-medium text-center'>
                    {w}
                  </div>
                ))}
              </div>
              <div className='grid grid-cols-7 gap-2'>
                {monthDays.map(({ date, inMonth }) => {
                  const key = toYMD(date);
                  const tripsForDay = calendarTripsByDay[key] || [];
                  const isToday = toYMD(date) === toYMD(new Date());
                  return (
                    <div
                      key={key + String(inMonth)}
                      className={
                        'min-h-[110px] border rounded-md p-2 flex flex-col gap-1 ' +
                        (inMonth ? '' : 'bg-default-100 opacity-60')
                      }
                    >
                      <div className='flex items-center justify-between'>
                        <span
                          className={'text-xs ' + (isToday ? 'font-bold' : '')}
                        >
                          {date.getDate()}
                        </span>
                        {isToday && (
                          <Chip size='sm' variant='flat'>
                            Hoy
                          </Chip>
                        )}
                      </div>
                      <div className='flex flex-col gap-1 mt-1'>
                        {tripsForDay.length === 0 ? (
                          <span className='text-[11px] text-default-300'>—</span>
                        ) : (
                          tripsForDay.slice(0, 3).map((t) => (
                            <div
                              key={t.id}
                              className='text-[11px] px-2 py-1 rounded bg-primary-50 text-primary-700 truncate'
                              title={`${t.ship?.name || 'Sin nave'} · ${t.market?.name || '-'} (${t.etd?.split('T')[0]} → ${t.eta?.split('T')[0]})`}
                            >
                              {(t.ship?.name || 'Sin nave') +
                                (t.market ? ` · ${t.market.id} - ${t.market.name} - ${t.market.country}` : '')}
                            </div>
                          ))
                        )}
                        {tripsForDay.length > 3 && (
                          <span className='text-[11px] text-default-400'>
                            +{tripsForDay.length - 3} más
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </Tab>

        <Tab key='resources' title='Recursos'>
          <div className='flex flex-col gap-4 mb-6'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='flex items-center gap-2'>
                <Button
                  variant='light'
                  onPress={() =>
                    setCalendarDate(
                      (d) => new Date(d.getFullYear(), d.getMonth() - 1, 1)
                    )
                  }
                >
                  Mes anterior
                </Button>
                <div className='font-semibold'>
                  {calendarDate.toLocaleString('es-ES', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
                <Button
                  variant='light'
                  onPress={() =>
                    setCalendarDate(
                      (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1)
                    )
                  }
                >
                  Mes siguiente
                </Button>
                <Button
                  variant='flat'
                  onPress={() => setCalendarDate(new Date())}
                >
                  Hoy
                </Button>
              </div>
              <div className='flex items-center gap-3'>
                <Input
                  placeholder='Buscar nave...'
                  value={resourceSearchTerm}
                  onChange={(e) => setResourceSearchTerm(e.target.value)}
                  startContent={<Icon icon='tabler:search' className='text-default-300' />}
                  className='w-72'
                />
                <Select
                  label='Tipo de nave'
                  className='min-w-[180px]'
                  selectedKeys={shipTypeFilter ? [shipTypeFilter] : []}
                  onChange={(e) => setShipTypeFilter(e.target.value)}
                >
                  <SelectItem key='' value=''>
                    Todos
                  </SelectItem>
                  <SelectItem key='A' value='A'>
                    Aéreo
                  </SelectItem>
                  <SelectItem key='M' value='M'>
                    Marítimo
                  </SelectItem>
                  <SelectItem key='T' value='T'>
                    Terrestre
                  </SelectItem>
                </Select>
                <Select
                  label='Ordenar por'
                  className='min-w-[180px]'
                  selectedKeys={[resourceSort]}
                  onChange={(e) =>
                    setResourceSort(e.target.value as 'name' | 'etd' | 'eta')
                  }
                >
                  <SelectItem key='name' value='name'>
                    Nombre
                  </SelectItem>
                  <SelectItem key='etd' value='etd'>
                    ETD
                  </SelectItem>
                  <SelectItem key='eta' value='eta'>
                    ETA
                  </SelectItem>
                </Select>
              </div>
            </div>

            <Card className='p-4 overflow-auto'>
              {/* Header: day numbers */}
              <div
                className='grid sticky top-0 bg-background z-20 pb-2'
                style={{ gridTemplateColumns: `220px 1fr` }}
              >
                <div className='text-sm font-semibold text-default-500 px-2'>
                  Nave
                </div>
                <div
                  className='grid gap-[2px]'
                  style={{
                    gridTemplateColumns: `repeat(${monthPureDays.length}, minmax(20px, 1fr))`,
                  }}
                >
                  {monthPureDays.map((d) => (
                    <div
                      key={toYMD(d)}
                      className='text-[11px] text-default-400 text-center'
                    >
                      {d.getDate()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Rows: ships with bars */}
              <div className='mt-2'>
                {resourceTimelineShips.map(({ ship: s, trips }) => (
                  <div
                    key={s.id}
                    className='grid items-center border-t py-2'
                    style={{ gridTemplateColumns: `220px 1fr` }}
                  >
                    <div className='px-2 sticky left-0 bg-background z-10'>
                      {s.name}
                    </div>
                    <div className='relative w-full h-12'>
                      {trips.slice(0, 3).map((t, idx) => {
                        const start = parseISODate(t.etd);
                        const end = parseISODate(t.eta);
                        const clampedStart =
                          start < monthStart ? monthStart : start;
                        const clampedEnd = end > monthEnd ? monthEnd : end;
                        const startIndex = Math.max(
                          0,
                          Math.min(
                            numDays - 1,
                            Math.floor(
                              (clampedStart.getTime() - monthStart.getTime()) /
                                (1000 * 60 * 60 * 24)
                            )
                          )
                        );
                        const spanDays =
                          Math.floor(
                            (clampedEnd.getTime() - clampedStart.getTime()) /
                              (1000 * 60 * 60 * 24)
                          ) + 1;
                        const leftPct = (startIndex / numDays) * 100;
                        const widthPct = (spanDays / numDays) * 100;
                        const topPx = idx * 18; // stack visually
                        return (
                          <div
                            key={t.id}
                            className='absolute rounded-md bg-primary-100 text-primary-800 text-[11px] px-2 py-1 whitespace-nowrap overflow-hidden'
                            style={{
                              left: `${leftPct}%`,
                              width: `${widthPct}%`,
                              top: topPx,
                            }}
                            title={`${s.name} · ${t.market?.name || '-'} (${t.etd?.split('T')[0] ?? '-'} → ${t.eta?.split('T')[0] ?? '-'})`}
                          >
                            {t.market?.id ? `${t.voyage_number ? `${t.voyage_number} ` : ''}· ${t.market?.id} - ${t.market?.name} - ${t.market?.country}` : '-'}
                          </div>
                        );
                      })}
                      {trips.length > 3 && (
                        <div className='absolute right-1 top-[54px] text-[11px] text-default-400'>
                          +{trips.length - 3} más
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </Tab>
      </Tabs>

      {/* Ship Edit/Create Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size='2xl'
        scrollBehavior='inside'
        classNames={{
          body: 'max-h-[70vh] overflow-auto',
        }}
      >
        <ModalContent>
          <ModalHeader>Editar Nave</ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <Input
                label='Nombre'
                value={newShipData.name}
                onChange={(e) =>
                  setNewShipData({ ...newShipData, name: e.target.value })
                }
              />
              <Input
                label='Compañía'
                value={newShipData.company}
                onChange={(e) =>
                  setNewShipData({ ...newShipData, company: e.target.value })
                }
              />
              <Select
                label='Tipo'
                selectedKeys={newShipData.type ? [newShipData.type] : []}
                onChange={(e) =>
                  setNewShipData({ ...newShipData, type: e.target.value })
                }
              >
                <SelectItem key='A' value='A'>
                  Aereo
                </SelectItem>
                <SelectItem key='M' value='M'>
                  Maritimo
                </SelectItem>
                <SelectItem key='T' value='T'>
                  Terrestre
                </SelectItem>
              </Select>

              {/* Ship Trips Section */}
              <div className='mt-4'>
                <div className='flex justify-between items-center mb-2'>
                  <h3 className='text-lg font-semibold'>Viajes Programados</h3>
                  <Button
                    size='sm'
                    color='primary'
                    onPress={() => newShipTripModal.onOpen()}
                    startContent={<Icon icon='tabler:plus' />}
                  >
                    Nuevo Viaje
                  </Button>
                </div>
                {editingShip?.ship_trips &&
                  editingShip?.ship_trips.length > 0 && (
                    <Table aria-label='Ship trips'>
                      <TableHeader>
                        <TableColumn>ID</TableColumn>
                        <TableColumn>Voyage Number</TableColumn>
                        <TableColumn>ETD</TableColumn>
                        <TableColumn>ETA</TableColumn>
                        <TableColumn>Origen</TableColumn>
                        <TableColumn>Mercado Dest.</TableColumn>
                        <TableColumn>Liquidaciones</TableColumn>
                        <TableColumn>Acciones</TableColumn>
                      </TableHeader>
                      <TableBody items={editingShip.ship_trips}>
                        {(trip) => (
                          <TableRow key={trip.id}>
                            <TableCell>{trip?.id}</TableCell>
                            <TableCell>{trip?.voyage_number}</TableCell>
                            <TableCell>{trip?.etd ? trip.etd.split('T')[0].split('-').reverse().join(' ') : '-'}</TableCell>
                            <TableCell>{trip?.eta ? trip.eta.split('T')[0].split('-').reverse().join(' ') : '-'}</TableCell>
                            <TableCell>{trip?.origin_market_id ? `${trip?.origin_market_id} - ${trip?.origin_market?.name} - ${trip?.origin_market?.country}` : '-'}</TableCell>
                            <TableCell>
                              {trip?.market
                                ? `${trip?.market?.id} - ${trip?.market?.name} - ${trip?.market?.country}`
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {trip?.settlements_count?.[0]?.count || 0}
                            </TableCell>
                            <TableCell>
                              <Button
                                size='sm'
                                color='primary'
                                variant='light'
                                onPress={() => {
                                  onClose();
                                  handleEditShipTrip(trip);
                                }}
                              >
                                Editar
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={onClose}>
              Cancelar
            </Button>
            <Button color='primary' onPress={handleSaveShip}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Ship Trip Edit Modal */}
      <Modal isOpen={shipTripModal.isOpen} onClose={shipTripModal.onClose}>
        <ModalContent>
          <ModalHeader>Editar Viaje</ModalHeader>
          <ModalBody>
            <Input
              label='Voyage Number'
              value={editingShipTripData.voyage_number}
              onChange={(e) =>
                setEditingShipTripData({
                  ...editingShipTripData,
                  voyage_number: e.target.value,
                })
              }
            />
            <Input
              type='date'
              label='ETD'
              value={editingShipTripData.etd}
              onChange={(e) =>
                setEditingShipTripData({
                  ...editingShipTripData,
                  etd: e.target.value,
                })
              }
            />
            <Input
              type='date'
              label='ETA'
              value={editingShipTripData.eta}
              onChange={(e) =>
                setEditingShipTripData({
                  ...editingShipTripData,
                  eta: e.target.value,
                })
              }
            />

            <CustomAutocomplete
              label='Origen'
              placeholder='Buscar origen...'
              options={marketOptions}
              selectedKey={editingShipTripData.origin_market_id || undefined}
              onSelectionChange={(key) =>
                setEditingShipTripData({
                  ...editingShipTripData,
                  origin_market_id: key ? String(key) : '',
                })
              }
            />
            <CustomAutocomplete
              label='Mercado'
              placeholder='Buscar mercado...'
              options={marketOptions}
              selectedKey={editingShipTripData.market_id || undefined}
              onSelectionChange={(key) =>
                setEditingShipTripData({
                  ...editingShipTripData,
                  market_id: key ? String(key) : '',
                })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={shipTripModal.onClose}
            >
              Cancelar
            </Button>
            <Button color='primary' onPress={handleSaveShipTrip}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Merge Modal */}
      <Modal isOpen={mergeModalOpen} onClose={() => setMergeModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Unir Naves</ModalHeader>
          <ModalBody>
            <Input
              label='Nombre de la nueva nave'
              value={newMergedShipName}
              onChange={(e) => setNewMergedShipName(e.target.value)}
            />
            <p className='text-sm text-default-400 mt-2'>
              Se unirán {selectedShips.length} naves y todos sus viajes
              asociados.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setMergeModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button color='danger' onPress={handleMergeShips}>
              Unir Naves
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add New Ship Trip Modal */}
      <Modal
        isOpen={newShipTripModal.isOpen}
        onClose={newShipTripModal.onClose}
      >
        <ModalContent>
          <ModalHeader>Crear Viaje para {editingShip?.name}</ModalHeader>
          <ModalBody>
            <Input
              label='Voyage Number'
              value={newShipTripData.voyage_number}
              onChange={(e) =>
                setNewShipTripData({
                  ...newShipTripData,
                  voyage_number: e.target.value,
                })
              }
            />
            <Input
              type='date'
              label='ETD'
              value={newShipTripData.etd}
              onChange={(e) =>
                setNewShipTripData({ ...newShipTripData, etd: e.target.value })
              }
            />
            <Input
              type='date'
              label='ETA'
              value={newShipTripData.eta}
              onChange={(e) =>
                setNewShipTripData({ ...newShipTripData, eta: e.target.value })
              }
            />

            <CustomAutocomplete
              label='Origen'
              placeholder='Buscar origen...'
              options={marketOptions}
              selectedKey={newShipTripData.origin_market_id || undefined}
              onSelectionChange={(key) =>
                setNewShipTripData({
                  ...newShipTripData,
                  origin_market_id: key ? String(key) : '',
                })
              }
            />

            <CustomAutocomplete
              label='Mercado'
              placeholder='Buscar mercado...'
              options={marketOptions}
              selectedKey={newShipTripData.market_id || undefined}
              onSelectionChange={(key) =>
                setNewShipTripData({
                  ...newShipTripData,
                  market_id: key ? String(key) : '',
                })
              }
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={newShipTripModal.onClose}
            >
              Cancelar
            </Button>
            <Button color='primary' onPress={handleCreateShipTrip}>
              Crear
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Trip Merge Modal */}
      <Modal
        isOpen={tripMergeModalOpen}
        onClose={() => setTripMergeModalOpen(false)}
      >
        <ModalContent>
          <ModalHeader>Unir Viajes</ModalHeader>
          <ModalBody>
            <Input
              type='date'
              label='Nueva fecha ETD'
              value={newMergedTripDates.etd}
              onChange={(e) =>
                setNewMergedTripDates({
                  ...newMergedTripDates,
                  etd: e.target.value,
                })
              }
            />
            <Input
              type='date'
              label='Nueva fecha ETA'
              value={newMergedTripDates.eta}
              onChange={(e) =>
                setNewMergedTripDates({
                  ...newMergedTripDates,
                  eta: e.target.value,
                })
              }
            />
            <Select
              label='Mercado'
              selectedKeys={
                newMergedTripDates.market_id
                  ? [newMergedTripDates.market_id]
                  : []
              }
              onChange={(e) =>
                setNewMergedTripDates({
                  ...newMergedTripDates,
                  market_id: e.target.value,
                })
              }
            >
              {markets.map((market) => (
                <SelectItem key={market.id} value={market.id}>
                  {`${market.id} - ${market.name} - ${market.country}`}
                </SelectItem>
              ))}
            </Select>
            <p className='text-sm text-default-400 mt-2'>
              Se unirán {selectedTrips.length} viajes y todas sus liquidaciones
              asociadas.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => setTripMergeModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button color='danger' onPress={handleMergeTrips}>
              Unir Viajes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        isOpen={bulkUploadModal.isOpen}
        onClose={() => {
          resetBulkUpload();
          bulkUploadModal.onClose();
        }}
        size='5xl'
        scrollBehavior='inside'
        classNames={{
          body: 'max-h-[75vh] overflow-auto',
        }}
      >
        <ModalContent>
          <ModalHeader>Importar naves y viajes</ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <div className='rounded-md border border-dashed border-default-300 p-4'>
                <p className='text-sm text-default-600 mb-3'>
                  La planilla debe incluir las columnas: Nave, Tipo, ETD, ETA,
                  ORIGEN LOCODE y DESTINO LOCODE.
                </p>
                <input
                  type='file'
                  accept='.xlsx,.xls'
                  onChange={handleBulkUploadFile}
                  disabled={isParsingBulkUpload || isConfirmingBulkUpload}
                  className='block w-full cursor-pointer rounded-md border border-default-200 bg-background text-sm text-default-700 file:mr-4 file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-white disabled:cursor-not-allowed disabled:opacity-50'
                />
                {bulkUploadFileName && (
                  <p className='text-sm text-default-500 mt-2'>
                    Archivo: {bulkUploadFileName}
                  </p>
                )}
              </div>

              {bulkUploadErrors.length > 0 && (
                <Alert variant='danger'>
                  {bulkUploadErrors.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </Alert>
              )}

              {missingMarketDrafts.length > 0 && (
                <div className='rounded-md border border-warning-200 bg-warning-50 p-4'>
                  <div className='flex flex-wrap items-center justify-between gap-3 mb-3'>
                    <div>
                      <h3 className='font-semibold text-warning-800'>
                        Mercados faltantes
                      </h3>
                      <p className='text-sm text-warning-700'>
                        Cree estos LOCODEs antes de confirmar naves y viajes.
                      </p>
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        size='sm'
                        variant='flat'
                        onPress={handleSuggestMissingMarkets}
                        isLoading={isSuggestingMarkets}
                        isDisabled={
                          isCreatingMarkets || missingMarketLocodes.length === 0
                        }
                      >
                        Sugerir con IA
                      </Button>
                      <Button
                        size='sm'
                        color='primary'
                        onPress={handleCreateMissingMarkets}
                        isLoading={isCreatingMarkets}
                        isDisabled={isSuggestingMarkets}
                      >
                        Crear mercados
                      </Button>
                    </div>
                  </div>

                  <div className='space-y-3'>
                    {missingMarketDrafts.map((draft) => (
                      <div
                        key={draft.id}
                        className='grid grid-cols-1 md:grid-cols-[110px_130px_1fr_1fr_160px] gap-2 items-center rounded-md bg-background p-3'
                      >
                        <Input
                          label='Locode'
                          value={draft.id}
                          isReadOnly
                          size='sm'
                        />
                        <Input
                          label='Country Code'
                          value={draft.country_code}
                          size='sm'
                          onChange={(e) =>
                            updateMissingMarketDraft(draft.id, {
                              country_code: normalizeLocode(e.target.value).slice(0, 2),
                            })
                          }
                        />
                        <Input
                          label='Nombre'
                          value={draft.name}
                          size='sm'
                          onChange={(e) =>
                            updateMissingMarketDraft(draft.id, {
                              name: e.target.value.toUpperCase(),
                            })
                          }
                        />
                        <Input
                          label='País'
                          value={draft.country}
                          size='sm'
                          onChange={(e) =>
                            updateMissingMarketDraft(draft.id, {
                              country: e.target.value.toUpperCase(),
                            })
                          }
                        />
                        <div className='flex items-center gap-2'>
                          <Switch
                            size='sm'
                            isSelected={draft.reviewed}
                            onValueChange={(reviewed) =>
                              updateMissingMarketDraft(draft.id, { reviewed })
                            }
                          />
                          <span className='text-sm'>Revisado</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkUploadPreviewRows.length > 0 && (
                <>
                  <div className='grid grid-cols-2 md:grid-cols-5 gap-2'>
                    <Card className='p-3'>
                      <p className='text-xs text-default-400'>Naves nuevas</p>
                      <p className='font-semibold'>
                        {bulkUploadStats.shipsToCreate}
                      </p>
                    </Card>
                    <Card className='p-3'>
                      <p className='text-xs text-default-400'>Viajes nuevos</p>
                      <p className='font-semibold'>
                        {bulkUploadStats.tripsToCreate}
                      </p>
                    </Card>
                    <Card className='p-3'>
                      <p className='text-xs text-default-400'>Ya existentes</p>
                      <p className='font-semibold'>
                        {bulkUploadStats.existingTrips}
                      </p>
                    </Card>
                    <Card className='p-3'>
                      <p className='text-xs text-default-400'>
                        Duplicados archivo
                      </p>
                      <p className='font-semibold'>
                        {bulkUploadStats.duplicateRows}
                      </p>
                    </Card>
                    <Card className='p-3'>
                      <p className='text-xs text-default-400'>Errores</p>
                      <p className='font-semibold'>
                        {bulkUploadStats.blockingErrors}
                      </p>
                    </Card>
                  </div>
                  <div className='flex flex-wrap items-center gap-2'>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={() => setAllCreateTripRowsSelected(true)}
                    >
                      Seleccionar todos los viajes nuevos
                    </Button>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={() => setAllCreateTripRowsSelected(false)}
                    >
                      Deseleccionar todos los viajes nuevos
                    </Button>
                    <span className='text-sm text-default-500'>
                      {selectedBulkTripRows.size} viajes nuevos seleccionados
                    </span>
                  </div>

                  <Table
                    aria-label='Vista previa importación naves'
                    key={`bulk-preview-${Array.from(selectedBulkTripRows)
                      .sort()
                      .join('|')}`}
                  >
                    <TableHeader>
                      <TableColumn>IMPORTAR</TableColumn>
                      <TableColumn>FILA</TableColumn>
                      <TableColumn>NAVE</TableColumn>
                      <TableColumn>TIPO</TableColumn>
                      <TableColumn>ETD</TableColumn>
                      <TableColumn>ETA</TableColumn>
                      <TableColumn>RUTA</TableColumn>
                      <TableColumn>NAVE</TableColumn>
                      <TableColumn>VIAJE</TableColumn>
                      <TableColumn>OBSERVACIONES</TableColumn>
                    </TableHeader>
                    <TableBody items={bulkUploadPreviewRows}>
                      {(row) => (
                        <TableRow
                          key={`${row.id}-${selectedBulkTripRows.has(row.id)}`}
                        >
                          <TableCell>
                            {row.tripAction === 'create' ? (
                              <input
                                type='checkbox'
                                className='h-4 w-4 cursor-pointer accent-primary'
                                checked={selectedBulkTripRows.has(row.id)}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) =>
                                  toggleBulkTripRow(row.id, event.target.checked)
                                }
                                aria-label={`Importar fila ${row.rowNumber}`}
                              />
                            ) : (
                              <span className='text-default-300'>-</span>
                            )}
                          </TableCell>
                          <TableCell>{row.rowNumber}</TableCell>
                          <TableCell>
                            <div className='max-w-[160px] truncate'>
                              {row.shipName || '-'}
                            </div>
                          </TableCell>
                          <TableCell>{row.shipType || '-'}</TableCell>
                          <TableCell>{row.etd || '-'}</TableCell>
                          <TableCell>{row.eta || '-'}</TableCell>
                          <TableCell>
                            {row.originLocode || '-'} →{' '}
                            {row.destinationLocode || '-'}
                          </TableCell>
                          <TableCell>
                            {row.shipAction === 'error' ? (
                              <Chip color='danger' size='sm' variant='flat'>
                                Error
                              </Chip>
                            ) : row.shipAction === 'reuse' ? (
                              <div className='flex flex-col gap-1'>
                                <Chip color='success' size='sm' variant='flat'>
                                  Usar existente
                                </Chip>
                                <span className='text-xs text-default-400'>
                                  {row.matchedShip?.name}{' '}
                                  {row.shipMatchScore
                                    ? `(${Math.round(row.shipMatchScore * 100)}%)`
                                    : ''}
                                </span>
                              </div>
                            ) : (
                              <StatusChip tone='info'>
                                Crear nave
                              </StatusChip>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.tripAction === 'error' ? (
                              <Chip color='danger' size='sm' variant='flat'>
                                Error
                              </Chip>
                            ) : row.tripAction === 'reuse' ? (
                              <div className='flex flex-col gap-1'>
                                <Chip color='success' size='sm' variant='flat'>
                                  Ya existe
                                </Chip>
                                <span className='text-xs text-default-400'>
                                  ID {row.matchedTrip?.id}
                                </span>
                              </div>
                            ) : row.tripAction === 'duplicate-file' ? (
                              <Chip color='warning' size='sm' variant='flat'>
                                Duplicado fila {row.duplicateOfRow}
                              </Chip>
                            ) : (
                              <StatusChip tone='info'>
                                Crear viaje
                              </StatusChip>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className='max-w-[260px] text-xs space-y-1'>
                              {row.errors.map((error) => (
                                <p key={error} className='text-danger'>
                                  {error}
                                </p>
                              ))}
                              {row.warnings.map((warning) => (
                                <p key={warning} className='text-warning'>
                                  {warning}
                                </p>
                              ))}
                              {row.errors.length === 0 &&
                                row.warnings.length === 0 && (
                                  <span className='text-default-400'>-</span>
                                )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                resetBulkUpload();
                bulkUploadModal.onClose();
              }}
              isDisabled={isConfirmingBulkUpload}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={handleConfirmBulkUpload}
              isLoading={isParsingBulkUpload || isConfirmingBulkUpload}
              isDisabled={
                bulkUploadPreviewRows.length === 0 ||
                bulkUploadStats.blockingErrors > 0 ||
                missingMarketLocodes.length > 0
              }
            >
              Confirmar importación
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Transfer Settlements Modal */}
      <Modal
        isOpen={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setOriginTripId(null);
          setDestinationTripId(null);
        }}
        size='4xl'
      >
        <ModalContent>
          <ModalHeader>
            Mueve Liquidaciones desde{' '}
            {shipTrips.find((trip) => trip.id === originTripId)?.ship?.name} -
            ETD:{' '}
            {
              shipTrips
                .find((trip) => trip.id === originTripId)
                ?.etd.split('T')[0]
            }{' '}
            - ETA:{' '}
            {
              shipTrips
                .find((trip) => trip.id === originTripId)
                ?.eta.split('T')[0]
            }{' '}
            - Mercado:{' '}
            {shipTrips.find((trip) => trip.id === originTripId)?.market?.name ||
              '-'}
          </ModalHeader>
          <ModalBody>
            <div className='space-y-4'>
              <p>Seleccione el viaje de destino:</p>
              <div className='form-group'>
                <label
                  htmlFor='destinationTrip'
                  className='block text-sm font-medium mb-1'
                >
                  Viaje de destino
                </label>
                <select
                  id='destinationTrip'
                  className='w-full px-3 py-2 border border-divider rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary'
                  value={destinationTripId || ''}
                  onChange={(e) => {
                    setDestinationTripId(Number(e.target.value) || null);
                  }}
                >
                  <option value=''>Seleccione un viaje</option>
                  {shipTrips
                    .filter((trip) => trip.id != originTripId)
                    .map((trip) => (
                      <option key={trip.id} value={trip.id}>
                        {`${trip?.ship?.name} - POD: ${trip?.market?.name || '-'} - ETD: ${trip?.etd ? trip?.etd?.split('T')[0] : '-'} - ETA: ${trip?.eta ? trip?.eta?.split('T')[0] : '-'}`}
                      </option>
                    ))}
                </select>
              </div>

              <p className='text-sm text-default-400'>
                Esta acción transferirá todas las liquidaciones del viaje de
                origen al viaje de destino.
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setTransferModalOpen(false);
                setOriginTripId(null);
                setDestinationTripId(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              color='primary'
              onPress={handleTransferSettlements}
              isDisabled={!originTripId || !destinationTripId}
            >
              Transferir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminShips;
